/**
 * AuthDebugPanel Component
 * 
 * DEV ONLY - Displays authentication debugging information
 * Shows active account, token preview, and decoded claims
 */

import React, { useState, useEffect } from 'react';
import { useMsal } from "@azure/msal-react";
import useAuthToken from "./useAuthToken";

/**
 * Decode JWT token without verification (for display only)
 */
const decodeJWT = (token) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (error) {
        console.error('[AuthDebugPanel] Failed to decode token:', error);
        return null;
    }
};

/**
 * AuthDebugPanel - Only shown in development mode
 */
const AuthDebugPanel = () => {
    const { accounts } = useMsal();
    const { token, getToken } = useAuthToken();
    const [decodedToken, setDecodedToken] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        // Automatically get token when component mounts
        if (accounts.length > 0 && !token) {
            getToken();
        }
    }, [accounts, token, getToken]);

    useEffect(() => {
        if (token) {
            const decoded = decodeJWT(token);
            setDecodedToken(decoded);
        }
    }, [token]);

    // Don't render in production, and require ?debugAuth=1 URL param even in dev
    // This ensures it never consumes screen space unless explicitly requested.
    const debugParam = typeof window !== 'undefined'
        && new URLSearchParams(window.location.search).get('debugAuth') === '1';
    if (process.env.NODE_ENV !== 'development' || !debugParam) {
        return null;
    }

    const activeAccount = accounts[0];

    if (!activeAccount) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    🔐 Auth Debug Panel
                </div>
                <div style={styles.body}>
                    <p style={styles.warning}>No active account</p>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div
                style={styles.header}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                🔐 Auth Debug Panel {isExpanded ? '▼' : '▶'}
            </div>

            {isExpanded && (
                <div style={styles.body}>
                    {/* Active Account */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Active Account</h4>
                        <div style={styles.info}>
                            <strong>Username:</strong> {activeAccount.username}
                        </div>
                        <div style={styles.info}>
                            <strong>Name:</strong> {activeAccount.name || 'N/A'}
                        </div>
                        <div style={styles.info}>
                            <strong>Home Account ID:</strong> {activeAccount.homeAccountId?.substring(0, 20)}...
                        </div>
                    </div>

                    {/* Access Token */}
                    <div style={styles.section}>
                        <h4 style={styles.sectionTitle}>Access Token</h4>
                        {token ? (
                            <>
                                <div style={styles.info}>
                                    <strong>Token (first 10 chars):</strong> {token.substring(0, 10)}...
                                </div>
                                <div style={styles.info}>
                                    <strong>Full Token:</strong>
                                    <textarea
                                        readOnly
                                        value={token}
                                        style={styles.textarea}
                                    />
                                </div>
                            </>
                        ) : (
                            <button onClick={getToken} style={styles.button}>
                                Get Access Token
                            </button>
                        )}
                    </div>

                    {/* Decoded Token Claims */}
                    {decodedToken && (
                        <div style={styles.section}>
                            <h4 style={styles.sectionTitle}>Token Claims</h4>
                            <div style={styles.info}>
                                <strong>Issuer (iss):</strong> {decodedToken.iss}
                            </div>
                            <div style={styles.info}>
                                <strong>Audience (aud):</strong> {decodedToken.aud}
                            </div>
                            <div style={styles.info}>
                                <strong>Scope (scp):</strong> {decodedToken.scp || 'N/A'}
                            </div>
                            <div style={styles.info}>
                                <strong>Subject (sub):</strong> {decodedToken.sub}
                            </div>
                            <div style={styles.info}>
                                <strong>Issued At:</strong> {new Date(decodedToken.iat * 1000).toLocaleString()}
                            </div>
                            <div style={styles.info}>
                                <strong>Expires:</strong> {new Date(decodedToken.exp * 1000).toLocaleString()}
                            </div>
                            <details style={{ marginTop: '10px' }}>
                                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                    All Claims (JSON)
                                </summary>
                                <pre style={styles.json}>
                                    {JSON.stringify(decodedToken, null, 2)}
                                </pre>
                            </details>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Styles
const styles = {
    container: {
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        backgroundColor: 'var(--bg-2)',
        color: 'var(--text-1)',
        borderRadius: '8px',
        boxShadow: 'var(--shadow)',
        zIndex: 9999,
        maxWidth: '500px',
        maxHeight: '80vh',
        overflow: 'auto',
    },
    header: {
        padding: '12px 16px',
        backgroundColor: 'var(--bg-3)',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        cursor: 'pointer',
        fontWeight: 'bold',
        userSelect: 'none',
    },
    body: {
        padding: '16px',
    },
    section: {
        marginBottom: '16px',
        paddingBottom: '16px',
        borderBottom: '1px solid var(--border)',
    },
    sectionTitle: {
        margin: '0 0 8px 0',
        color: 'var(--brand-2)',
    },
    info: {
        marginBottom: '8px',
        fontSize: '13px',
        wordBreak: 'break-all',
    },
    textarea: {
        width: '100%',
        height: '80px',
        marginTop: '8px',
        padding: '8px',
        backgroundColor: 'var(--bg-3)',
        color: 'var(--text-1)',
        border: '1px solid var(--border)',
        borderRadius: '4px',
        fontSize: '11px',
        fontFamily: 'monospace',
        resize: 'vertical',
    },
    json: {
        backgroundColor: 'var(--bg-3)',
        padding: '12px',
        borderRadius: '4px',
        fontSize: '11px',
        overflow: 'auto',
        maxHeight: '300px',
    },
    button: {
        padding: '8px 16px',
        backgroundColor: 'var(--brand-2)',
        color: 'var(--text-1)',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '13px',
    },
    warning: {
        color: 'var(--danger)',
        fontWeight: 'bold',
    },
};

export default AuthDebugPanel;
